<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { useCompetenceAssessment } from '@/composables/competenceAssessment/useCompetenceAssessment';
import { useCompetenceDomain } from '@/composables/useCompetenceDomain';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import ThreeDotsActionDialog from '@/core/dialogs/ThreeDotsActionDialog.vue';
import CompetenceDisplayList from '@/core/competences/CompetenceDisplayList.vue';
import { toSelectedCompetenceDisplayItems } from '@/core/competences/competenceDisplay.utils';
import PerformanceLevelRatingInput from '@/core/performance-visuals/PerformanceLevelRatingInput.vue';
import ThumbsUpIcon from '@/assets/icons/ThumbsUpIcon.vue';
import ThumbsUpFilledIcon from '@/assets/icons/ThumbsUpFilledIcon.vue';
import StudentRecipientSummary from '@/core/elements/StudentRecipientSummary.vue';
import ExternalMaterialList from '@/core/supportMeasures/ExternalMaterialList.vue';
import { IDropdownListAction, type ISelectedCompetence } from '@/interfaces';
import { toCompetenceSelectionRecord } from '@/utils/competence.domain.utils';
import { getDateForObservationTimeline } from '@/utils/date.utils';
import { findFocusedStudent } from '@/utils/students.utils';
import { EIcon } from 'common/enum/misc.enum';
import { toRecord } from 'common/utils/array.utils';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

type TProtocolEntry = Components.Schemas.SupportMeasureProtocolEntryDto;
type TRating = Components.Schemas.SupportMeasureProtocolEntryRating;

const props = defineProps<{
  protocol: TProtocolEntry;
  schoolSubjectId: number;
  readonly?: boolean;
  /** Student view: names this student and counts the other recipients. */
  selectedStudentId?: number | null;
}>();

const emit = defineEmits<{
  'request-edit': [];
  'request-delete': [];
  rate: [rating: TRating | null];
  // inline rating change in the timeline (removal/adding competences still happens via the edit composer)
  'update:observation-level': [payload: { competenceId: number; level: number | null }];
}>();

const { whoAmI } = storeToRefs(useWhoAmIStore());
const { allCompetenceDomains } = useCompetenceDomain();
const { currentCompetenceAssessment } = useCompetenceAssessment();
const { getSchoolSubjectColor } = useSchoolSubject();

const competenceAssessment = computed(() => currentCompetenceAssessment.value ?? undefined);

const competenceRowById = computed(() =>
  toCompetenceSelectionRecord(
    allCompetenceDomains.value.filter((domain) => domain.schoolSubjectId === props.schoolSubjectId),
  ),
);

// one row per rated competence (levels are uniform across students). A class has a single competence
// assessment that can't change once observations exist, so currentCompetenceAssessment is always the one
// each observation was recorded under.
const observationRows = computed<{ competence: ISelectedCompetence; level: number | null }[]>(() => {
  const byCompetence = new Map<number, { competence: ISelectedCompetence; level: number | null }>();
  for (const observation of props.protocol.competenceObservations) {
    if (byCompetence.has(observation.competenceId)) continue;
    const competence = competenceRowById.value[observation.competenceId];
    if (!competence) continue;
    byCompetence.set(observation.competenceId, { competence, level: observation.performanceLevelV2Id });
  }
  return [...byCompetence.values()];
});
const competenceItems = computed(() =>
  toSelectedCompetenceDisplayItems(
    observationRows.value.map(({ competence }) => competence),
    getSchoolSubjectColor,
  ),
);
const competenceLevelById = computed<Partial<Record<number, number | null>>>(() =>
  toRecord(
    observationRows.value,
    ({ competence }) => competence.competence.id,
    ({ level }) => level,
  ),
);

const canModify = computed<boolean>(
  () => !props.readonly && props.protocol.creator?.id != null && props.protocol.creator.id === whoAmI.value?.id,
);

const onRate = (value: TRating): void => {
  if (!canModify.value) return;
  emit('rate', props.protocol.rating === value ? null : value);
};

const RATING_BUTTON_BASE = 'flex items-center justify-center rounded-full transition-colors';

const positiveButtonClass = computed<string>(() => {
  if (props.protocol.rating === 'positive') return `${RATING_BUTTON_BASE} text-tint`;
  return canModify.value
    ? `${RATING_BUTTON_BASE} text-secondary hover:text-tint active:text-tint`
    : `${RATING_BUTTON_BASE} text-secondary cursor-default`;
});

const negativeButtonClass = computed<string>(() => {
  if (props.protocol.rating === 'negative') return `${RATING_BUTTON_BASE} text-error`;
  return canModify.value
    ? `${RATING_BUTTON_BASE} text-secondary hover:text-error active:text-error`
    : `${RATING_BUTTON_BASE} text-secondary cursor-default`;
});

const positiveIcon = computed(() => (props.protocol.rating === 'positive' ? ThumbsUpFilledIcon : ThumbsUpIcon));
const negativeIcon = computed(() => (props.protocol.rating === 'negative' ? ThumbsUpFilledIcon : ThumbsUpIcon));

const { t } = useI18n();

const entryDateLabel = computed<string>(() => getDateForObservationTimeline(props.protocol.date));

const focusedStudent = computed(() => findFocusedStudent(props.protocol.students, props.selectedStudentId));

const actions = computed<IDropdownListAction[]>(() => [
  { label: t('general.edit'), iconBefore: EIcon.EDIT, action: () => emit('request-edit') },
  { label: t('general.delete'), iconBefore: EIcon.DELETE, style: 'error', action: () => emit('request-delete') },
]);
</script>

<template>
  <div class="rounded-group bg-surface-secondary p-md">
    <div class="flex items-center justify-between gap-sm">
      <span class="text-secondary text-footer font-semibold">{{ entryDateLabel }}</span>
      <ThreeDotsActionDialog v-if="canModify" :actions="actions" :min-width="220" size="sm" />
    </div>

    <p class="text-body mt-xs whitespace-pre-line break-words">{{ protocol.text }}</p>

    <!-- ratings are editable inline; removing/adding competences happens via the edit composer -->
    <div v-if="competenceItems.length && competenceAssessment" class="mt-md">
      <CompetenceDisplayList :items="competenceItems">
        <template #trailing="{ item }">
          <PerformanceLevelRatingInput
            :accessible-label="item.description"
            :disabled="!canModify"
            :model-value="competenceLevelById[item.id] ?? null"
            :performance-visual="competenceAssessment.performanceVisual"
            @update:model-value="emit('update:observation-level', { competenceId: item.id, level: $event })"
          />
        </template>
      </CompetenceDisplayList>
    </div>

    <ExternalMaterialList
      v-if="protocol.materials.length"
      class="mt-md"
      :materials="protocol.materials"
      density="protocol"
      surface="primary"
    />

    <StudentRecipientSummary class="mt-md" :focused-student="focusedStudent" :students="protocol.students" />

    <div v-if="canModify || protocol.rating" class="mt-md flex flex-wrap items-center gap-sm">
      <span class="text-secondary text-footer">{{ $t('supportMeasureEntry.rating.question') }}</span>
      <div class="flex items-center gap-md">
        <button
          type="button"
          :disabled="!canModify"
          :aria-pressed="protocol.rating === 'positive'"
          :aria-label="$t('supportMeasureEntry.rating.helpful')"
          :class="positiveButtonClass"
          @click="onRate('positive')"
        >
          <component :is="positiveIcon" class="size-icon-xs" />
        </button>
        <button
          type="button"
          :disabled="!canModify"
          :aria-pressed="protocol.rating === 'negative'"
          :aria-label="$t('supportMeasureEntry.rating.notHelpful')"
          :class="negativeButtonClass"
          @click="onRate('negative')"
        >
          <component :is="negativeIcon" class="size-icon-xs rotate-180" />
        </button>
      </div>
    </div>
  </div>
</template>
